import React, { useState } from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BarChartIcon from "@mui/icons-material/BarChart";
import PieChartIcon from "@mui/icons-material/PieChart";
import ScatterPlotIcon from "@mui/icons-material/ScatterPlot";
import FunctionsIcon from "@mui/icons-material/Functions";
import InsertChartIcon from "@mui/icons-material/InsertChart";
import TimelineIcon from "@mui/icons-material/Timeline";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import { useNavigate } from "react-router-dom";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { atomDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { motion } from "framer-motion";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  PieChart as RePieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
  ResponsiveContainer,
} from "recharts";

// ✅ Chart Renderer Component
const ChartRenderer = ({ type, data }) => {
  if (!data) return null;

  switch (type) {
    case "bar":
      return (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data}>
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="age" fill="#1A9E7F" />
          </BarChart>
        </ResponsiveContainer>
      );
    case "pie":
      return (
        <ResponsiveContainer width="100%" height={300}>
          <RePieChart>
            <Pie data={data} cx="50%" cy="50%" outerRadius={80} label dataKey="value">
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={["#034522", "#1A9E7F", "#4caf50"][index % 3]} />
              ))}
            </Pie>
            <Tooltip />
          </RePieChart>
        </ResponsiveContainer>
      );
    case "line":
      return (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data}>
            <XAxis dataKey="x" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="y" stroke="#1A9E7F" strokeWidth={3} />
          </LineChart>
        </ResponsiveContainer>
      );
    case "scatter":
      return (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data}>
            <XAxis dataKey="x" />
            <YAxis />
            <Tooltip />
            <Line type="monotone" dataKey="y" stroke="#ff9800" dot strokeWidth={2} />
          </LineChart>
        </ResponsiveContainer>
      );
    default:
      return null;
  }
};

// ✅ Data Analysis Content Data
const dataAnalysisContent = [
  {
    section: "Data Analysis Basics",
    icon: <BarChartIcon />,
    items: [
      {
        name: "Matplotlib",
        description: "Python plotting library for data visualization.",
        fullContent: [
          {
            heading: "Overview",
            text: "Matplotlib is a fundamental plotting library for Python that allows creation of static, animated, and interactive plots.",
          },
          {
            heading: "Key Features",
            points: [
              "Line charts, bar plots, scatter plots",
              "Histograms & pie charts",
              "Customization of axes, colors, and styles",
              "Integration with Jupyter notebooks",
            ],
          },
          {
            heading: "Example (Python - Simple Plot)",
            code: `import matplotlib.pyplot as plt

x = [1, 2, 3, 4, 5]
y = [2, 4, 6, 8, 10]

plt.plot(x, y, marker="o", color="blue")
plt.title("Simple Line Plot")
plt.xlabel("X-axis")
plt.ylabel("Y-axis")
plt.show()`,
            language: "python",
          },
        ],
        chartType: "line",
        chartData: [
          { x: 1, y: 2 },
          { x: 2, y: 4 },
          { x: 3, y: 6 },
          { x: 4, y: 8 },
          { x: 5, y: 10 },
        ],
      },
      {
        name: "Seaborn",
        description: "Statistical data visualization with beautiful defaults.",
        fullContent: [
          {
            heading: "Overview",
            text: "Seaborn is built on top of Matplotlib and adds a high-level interface for attractive, informative statistical plots.",
          },
          {
            heading: "Key Features",
            points: ["Heatmaps", "Box plots", "Violin plots", "Pair plots", "Correlation matrices"],
          },
          {
            heading: "Example (Python - Heatmap)",
            code: `import seaborn as sns
import matplotlib.pyplot as plt

data = sns.load_dataset("iris")
corr = data.corr()

sns.heatmap(corr, annot=True, cmap="coolwarm")
plt.show()`,
            language: "python",
          },
        ],
        chartType: "pie",
        chartData: [
          { name: "Positive", value: 55 },
          { name: "Neutral", value: 30 },
          { name: "Negative", value: 15 },
        ],
      },
      {
        name: "NumPy",
        description: "Python library for numerical computing.",
        fullContent: [
          {
            heading: "Overview",
            text: "NumPy provides powerful n-dimensional arrays and mathematical functions for numerical operations.",
          },
          {
            heading: "Key Features",
            points: ["Fast matrix operations", "Broadcasting support", "Random number generation", "Linear algebra utilities"],
          },
          {
            heading: "Example (Python - NumPy Array)",
            code: `import numpy as np

arr = np.array([1, 2, 3, 4, 5])
print("Array:", arr)
print("Mean:", np.mean(arr))
print("Standard Deviation:", np.std(arr))`,
            language: "python",
          },
        ],
        chartType: "bar",
        chartData: [
          { name: "A", age: 10 },
          { name: "B", age: 20 },
          { name: "C", age: 30 },
        ],
      },
    ],
  },
  {
    section: "Intermediate Data Processing",
    icon: <PieChartIcon />,
    items: [
      {
        name: "Pandas",
        description: "Data manipulation and analysis.",
        fullContent: [
          {
            heading: "Overview",
            text: "Pandas provides the DataFrame structure, allowing intuitive handling of tabular data.",
          },
          {
            heading: "Example (Python - Pandas DataFrame)",
            code: `import pandas as pd

data = {"Name": ["Alice", "Bob", "Charlie"], "Age": [25, 30, 35]}
df = pd.DataFrame(data)
print(df)`,
            language: "python",
          },
        ],
        chartType: "bar",
        chartData: [
          { name: "Alice", age: 25 },
          { name: "Bob", age: 30 },
          { name: "Charlie", age: 35 },
        ],
      },
      {
        name: "SQL in Python",
        description: "Using SQLite/SQLAlchemy inside Python.",
        fullContent: [
          {
            heading: "Overview",
            text: "Python can connect to relational databases via SQLite or SQLAlchemy, making it possible to run SQL queries on structured data.",
          },
          {
            heading: "Example (Python - SQLite)",
            code: `import sqlite3

conn = sqlite3.connect(":memory:")
cursor = conn.cursor()

cursor.execute("CREATE TABLE users (id INT, name TEXT)")
cursor.execute("INSERT INTO users VALUES (1, 'Alice')")
cursor.execute("SELECT * FROM users")

print(cursor.fetchall())`,
            language: "python",
          },
        ],
      },
      {
        name: "Data Cleaning",
        description: "Handling missing values, duplicates, and formatting issues.",
        fullContent: [
          {
            heading: "Overview",
            text: "Cleaning datasets is an essential step to improve data quality before applying analytics or machine learning.",
          },
          {
            heading: "Example (Python - Handling Missing Values)",
            code: `import pandas as pd
import numpy as np

df = pd.DataFrame({"Name": ["Alice", "Bob", None], "Age": [25, np.nan, 35]})
df_cleaned = df.dropna()
print(df_cleaned)`,
            language: "python",
          },
        ],
      },
    ],
  },
  {
    section: "Advanced Data Visualization",
    icon: <InsertChartIcon />,
    items: [
      {
        name: "Plotly",
        description: "Interactive visualizations in Python.",
        fullContent: [
          {
            heading: "Overview",
            text: "Plotly supports interactive and web-based plots including scatter, bar, 3D charts, and dashboards.",
          },
          {
            heading: "Example (Python - Plotly Scatter)",
            code: `import plotly.express as px
df = px.data.iris()
fig = px.scatter(df, x="sepal_width", y="sepal_length", color="species")
fig.show()`,
            language: "python",
          },
        ],
        chartType: "scatter",
        chartData: [
          { x: 1, y: 2 },
          { x: 2, y: 3 },
          { x: 3, y: 5 },
          { x: 4, y: 7 },
        ],
      },
      {
        name: "Tableau",
        description: "BI tool for dashboards and analytics.",
        fullContent: [
          {
            heading: "Overview",
            text: "Tableau provides interactive dashboards and visualization for large datasets. Connect to many data sources and publish dashboards to Tableau Server or Tableau Online.",
          },
        ],
      },
      {
        name: "Streamlit",
        description: "Python framework for quick dashboards.",
        fullContent: [
          {
            heading: "Example (Python - Streamlit App)",
            code: `import streamlit as st

st.title("Simple Dashboard")
st.line_chart([1, 2, 3, 4, 5])`,
            language: "python",
          },
        ],
      },
      {
        name: "Power BI",
        description: "Business Intelligence and dashboarding for decision-making.",
        fullContent: [
          {
            heading: "Overview",
            text: "Power BI is Microsoft’s flagship BI platform. It enables organizations to collect, transform, and visualize data from multiple sources into interactive dashboards. It supports drag-and-drop visuals plus advanced M (Power Query) and DAX for transformation and calculations.",
          },
          {
            heading: "Key Features",
            points: [
              "Integration with Excel, SQL Server, Azure, and many connectors",
              "Drag-and-drop dashboard creation with scheduled refresh",
              "Custom visuals, maps, KPIs and slicers",
              "Row-level security and workspace sharing",
              "Natural language Q&A & AI insights",
            ],
          },
          {
            heading: "Example (Power Query - M Script)",
            code: `let
  Source = Csv.Document(
    File.Contents("C:\\Data\\SalesData.csv"),
    [Delimiter=",", Columns=3, Encoding=65001, QuoteStyle=QuoteStyle.None]
  ),
  PromotedHeaders = Table.PromoteHeaders(Source, [PromoteAllScalars=true]),
  ChangedType = Table.TransformColumnTypes(
    PromotedHeaders,
    {{"Sales", Int64.Type}, {"Profit", Int64.Type}, {"Region", type text}}
  )
in
  ChangedType`,
            language: "sql",
          },
          {
            heading: "Real-World Use Cases",
            points: [
              "Executive dashboards showing KPIs (sales, revenue, profit margins)",
              "Financial forecasting with AI insights",
              "Customer churn and retention analysis",
              "Marketing funnel optimization",
              "Operational monitoring in manufacturing and logistics",
            ],
          },
        ],
        chartType: "pie",
        chartData: [
          { name: "Sales", value: 500 },
          { name: "Profit", value: 200 },
          { name: "Loss", value: 50 },
        ],
      },
    ],
  },
  {
    section: "Machine Learning for Data Analytics",
    icon: <FunctionsIcon />,
    items: [
      {
        name: "Scikit-Learn",
        description: "ML for regression and classification.",
        fullContent: [
          {
            heading: "Example (Python - Logistic Regression)",
            code: `from sklearn.linear_model import LogisticRegression
from sklearn.datasets import load_iris
from sklearn.model_selection import train_test_split

iris = load_iris()
X_train, X_test, y_train, y_test = train_test_split(
    iris.data, iris.target, random_state=42
)

model = LogisticRegression(max_iter=200)
model.fit(X_train, y_train)
print("Accuracy:", model.score(X_test, y_test))`,
            language: "python",
          },
        ],
      },
      {
        name: "Time Series (Prophet)",
        description: "Forecasting with Prophet.",
        fullContent: [
          {
            heading: "Overview",
            text: "Prophet (by Meta) makes it easier to build robust forecasts with seasonality and holiday effects, commonly used for business time series forecasting.",
          },
        ],
      },
    ],
  },
  {
    section: "Big Data & Cloud Analytics",
    icon: <LeaderboardIcon />,
    items: [
      {
        name: "PySpark",
        description: "Distributed data processing.",
        fullContent: [
          {
            heading: "Example (Python - PySpark)",
            code: `from pyspark.sql import SparkSession

spark = SparkSession.builder.appName("Example").getOrCreate()
data = [("Alice", 25), ("Bob", 30)]
df = spark.createDataFrame(data, ["Name", "Age"])
df.show()`,
            language: "python",
          },
        ],
      },
      {
        name: "BigQuery",
        description: "Google's cloud data warehouse.",
        fullContent: [
          {
            heading: "Overview",
            text: "BigQuery is a serverless, highly scalable, low-cost enterprise data warehouse for analytics. Use SQL to analyze petabytes of data.",
          },
        ],
      },
    ],
  },
  {
    section: "Real-World Data Engineering Tools",
    icon: <TimelineIcon />,
    items: [
      {
        name: "Airflow",
        description: "ETL workflow orchestration.",
        fullContent: [
          {
            heading: "Overview",
            text: "Apache Airflow is used to author, schedule, and monitor workflows as directed acyclic graphs (DAGs). It orchestrates ETL steps and complex pipelines.",
          },
        ],
      },
      {
        name: "Kafka",
        description: "Real-time streaming platform.",
        fullContent: [
          {
            heading: "Overview",
            text: "Apache Kafka is a distributed streaming platform used for building real-time data pipelines and streaming applications.",
          },
        ],
      },
    ],
  },
  {
    section: "Blockchain Data Analytics",
    icon: <ScatterPlotIcon />,
    items: [
      {
        name: "Ethereum Balance Fetch (Web3.js)",
        description: "Fetch account balances from Ethereum blockchain using Web3.js.",
        fullContent: [
          {
            heading: "Overview",
            text: "Web3.js allows you to connect to Ethereum nodes and query blockchain data for balances, transactions, and smart contracts.",
          },
          {
            heading: "Key Features",
            points: [
              "Fetch wallet balances",
              "Query transactions",
              "Interact with smart contracts",
              "Supports Ethereum-compatible chains",
            ],
          },
          {
            heading: "Example (Web3.js - Fetch Balance)",
            code: `const Web3 = require("web3");

// Connect to Ethereum node (Infura endpoint used here)
const web3 = new Web3("https://mainnet.infura.io/v3/YOUR_INFURA_KEY");

async function getBalance() {
  const address = "0xde0B295669a9FD93d5F28D9Ec85E40f4cb697BAe";
  const balance = await web3.eth.getBalance(address);
  console.log("ETH Balance:", web3.utils.fromWei(balance, "ether"));
}

getBalance();`,
            language: "javascript",
          },
        ],
        chartType: "bar",
        chartData: [
          { name: "Wallet A", age: 5 },
          { name: "Wallet B", age: 12 },
          { name: "Wallet C", age: 8 },
        ],
      },
      {
        name: "Smart Contract Analytics (Web3.js)",
        description: "Query smart contract events using Web3.js.",
        fullContent: [
          {
            heading: "Overview",
            text: "Web3.js can subscribe to contract events and logs, which are critical for analytics platforms that monitor DeFi, NFTs, or DAOs.",
          },
          {
            heading: "Example (Web3.js - Contract Event Logs)",
            code: `const Web3 = require("web3");
const web3 = new Web3("https://mainnet.infura.io/v3/YOUR_INFURA_KEY");

const contractABI = [/* ABI JSON */];
const contractAddress = "0x123456789...";

const contract = new web3.eth.Contract(contractABI, contractAddress);

contract.getPastEvents(
  "Transfer",
  { fromBlock: 17000000, toBlock: "latest" }
).then(events => {
  console.log("Past transfer events:", events);
});`,
            language: "javascript",
          },
        ],
      },
      {
        name: "NFT Metadata Analysis (Web3.js)",
        description: "Extract metadata from NFTs and analyze collections.",
        fullContent: [
          {
            heading: "Example (Web3.js - Fetch Token URI)",
            code: `const Web3 = require("web3");
const web3 = new Web3("https://mainnet.infura.io/v3/YOUR_INFURA_KEY");

const nftABI = [/* ERC721 ABI */];
const nftAddress = "0xABC123...";

const nftContract = new web3.eth.Contract(nftABI, nftAddress);

async function getNFTMetadata(tokenId) {
  const tokenURI = await nftContract.methods.tokenURI(tokenId).call();
  console.log("NFT Metadata URI:", tokenURI);
}

getNFTMetadata(1);`,
            language: "javascript",
          },
        ],
      },
    ],
  },
];

function DataAnalysis() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Box sx={{ bgcolor: "#f4f6f8", minHeight: "100vh", pt: 12, pb: 8 }}>
      <Container maxWidth="xl">
        {/* Header Navigation */}
        <Box mb={4} display="flex" alignItems="center">
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{
              fontWeight: 700,
              color: "#034522",
              mr: 2,
              textTransform: "none",
              borderRadius: "50px",
              px: 3,
              "&:hover": { bgcolor: "rgba(3, 69, 34, 0.1)" },
            }}
          >
            Back to Services
          </Button>
          <Typography variant="h4" fontWeight={800} color="#1a1a1a" sx={{ fontSize: { xs: "1.5rem", md: "2.125rem" } }}>
            Data Analysis
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {/* Sidebar Tabs (Desktop) / Top Tabs (Mobile) */}
          <Grid item xs={12} md={3}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                overflow: "hidden",
                boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                background: "#fff",
              }}
            >
              <Tabs
                orientation={isMobile ? "horizontal" : "vertical"}
                variant="scrollable"
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  borderRight: { md: 1, xs: 0 },
                  borderColor: "divider",
                  "& .MuiTabs-indicator": {
                    backgroundColor: "#1A9E7F",
                    width: "4px",
                    left: { md: 0 },
                    right: "auto"
                  },
                }}
              >
                {dataAnalysisContent.map((section, index) => (
                  <Tab
                    key={index}
                    icon={<Box sx={{ color: activeTab === index ? "#1A9E7F" : "inherit" }}>{section.icon}</Box>}
                    iconPosition="start"
                    label={section.section}
                    sx={{
                      alignItems: "center",
                      justifyContent: "flex-start",
                      textTransform: "none",
                      fontWeight: 600,
                      color: activeTab === index ? "#034522" : "#666",
                      fontSize: "1rem",
                      py: 3,
                      px: 3,
                      minHeight: 64,
                      transition: "all 0.3s",
                      "&.Mui-selected": {
                        color: "#034522",
                        bgcolor: "rgba(26, 158, 127, 0.08)",
                      },
                      "&:hover": {
                        bgcolor: "rgba(0,0,0,0.02)",
                      }
                    }}
                  />
                ))}
              </Tabs>
            </Paper>
          </Grid>

          {/* Main Content Area */}
          <Grid item xs={12} md={9}>
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  borderRadius: 4,
                  boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
                  minHeight: "60vh",
                  bgcolor: "#fff"
                }}
              >
                <Box mb={4} display="flex" alignItems="center">
                    <Box 
                        sx={{ 
                            p: 1.5, 
                            borderRadius: '12px', 
                            bgcolor: 'rgba(3, 69, 34, 0.1)', 
                            color: '#034522',
                            mr: 2,
                            display: 'flex',
                            boxShadow: '0 4px 12px rgba(3, 69, 34, 0.1)'
                        }}
                    >
                        {dataAnalysisContent[activeTab].icon}
                    </Box>
                    <Typography variant="h5" fontWeight={800} color="#034522">
                    {dataAnalysisContent[activeTab].section}
                    </Typography>
                </Box>
                
                <Box>
                  {dataAnalysisContent[activeTab].items.map((item, i) => (
                    <Accordion
                      key={i}
                      disableGutters
                      elevation={0}
                      sx={{
                        mb: 2,
                        borderRadius: "16px !important",
                        border: "1px solid #edf2f7",
                        "&:before": { display: "none" },
                        "&:hover": { bgcolor: "#f8f9fa", borderColor: "#e2e8f0" },
                        transition: "all 0.2s"
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: "#1A9E7F" }} />}
                        sx={{ px: 3, py: 1 }}
                      >
                        <Box>
                          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem', color: '#1a1a1a' }}>
                            {item.name}
                          </Typography>
                          <Typography variant="body2" color="#666" sx={{ mt: 0.5 }}>
                            {item.description}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 3, pb: 4, pt: 1, borderTop: '1px solid #f0f0f0' }}>
                        {item.fullContent.map((content, idx) => (
                          <Box key={idx} mt={3}>
                            <Typography
                              variant="subtitle1"
                              fontWeight={700}
                              gutterBottom
                              color="#1A9E7F"
                              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                            >
                                <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#1A9E7F' }} />
                                {content.heading}
                            </Typography>
                            {content.text && (
                              <Typography paragraph variant="body2" color="#4a5568" mb={2} sx={{ lineHeight: 1.7 }}>
                                {content.text}
                              </Typography>
                            )}
                            {content.points && (
                                <ul style={{ marginLeft: "1.5rem", marginTop: 0, marginBottom: '16px', color: '#4a5568' }}>
                                    {content.points.map((point, pIdx) => (
                                        <li key={pIdx}>
                                            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>{point}</Typography>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {content.code && (
                              <Box
                                sx={{
                                  borderRadius: 3,
                                  overflow: "hidden",
                                  border: "1px solid #e2e8f0",
                                  marginTop: 1,
                                  boxShadow: "0 4px 6px rgba(0,0,0,0.02)"
                                }}
                              >
                                <SyntaxHighlighter
                                  language={content.language || "javascript"}
                                  style={atomDark}
                                  customStyle={{ margin: 0, padding: "20px", fontSize: "0.9rem", lineHeight: 1.5 }}
                                >
                                  {content.code}
                                </SyntaxHighlighter>
                              </Box>
                            )}
                          </Box>
                        ))}
                        
                        {/* Render Chart if available for this item */}
                        {item.chartData && (
                          <Box sx={{ mt: 4, p: 3, bgcolor: '#f9f9f9', borderRadius: 4, border: '1px solid #eee' }}>
                            <Typography variant="h6" fontWeight={700} gutterBottom color="#1a1a1a" sx={{ mb: 3 }}>
                              Visualization Example
                            </Typography>
                            <ChartRenderer type={item.chartType} data={item.chartData} />
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default DataAnalysis;
